import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { getLastEmail, setLastEmail } from "../lib/lastEmail.js";
import { Field } from "./login/Field.js";
import { EyeIcon, EyeOffIcon } from "./login/icons.js";
import { LoginAurora } from "./login/LoginAurora.js";
import { LoginHero } from "./login/LoginHero.js";
import { TextLink } from "./login/TextLink.js";
import { useLoginIntro } from "./login/useLoginIntro.js";

/**
 * Sign-in screen — a dark XOP-brand "command center" hero.
 *
 * This file owns the auth flow: the email/password + two-step (TOTP / emailed
 * code) state machine, "remember last email", and the submit/resend/back
 * handlers. The bespoke entrance choreography lives in `login/useLoginIntro`;
 * the glows + logo/slogan chrome in `login/LoginAurora` + `login/LoginHero`;
 * and the form atoms in `login/Field` / `TextLink` / `icons`.
 *
 * The entrance rides a small set of named globals.css branding/keyframe hooks
 * (`login-aurora`, `focus-in`, `logo-halo`, `slogan-glyph[--write]`,
 * `shadow-glow`) — those are kept verbatim; every Tailwind utility / OKLCH
 * token class is converted to MUI `sx` + palette.
 */
export function LoginPage(): JSX.Element {
  const { login, user, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state && typeof location.state === "object" && "from" in location.state
      ? (location.state as { from: string }).from
      : "/") || "/";

  // Pre-fill the Email field with the address that last signed in. The
  // desktop webview (`tauri://` origin) gets no OS password-manager
  // autofill, so without this staff retype their email every launch.
  const [email, setEmail] = useState(() => getLastEmail());
  const [password, setPassword] = useState("");
  // True only on the first render — drives the initial autofocus split
  // (password when the email was pre-filled, else email). Captured once so
  // typing into the email field can't later steal focus on a re-render.
  const hadRememberedEmail = useRef(email !== "");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [needsOtp, setNeedsOtp] = useState(false);
  // Which second factor the upstream is challenging for, and (for email)
  // the address it just sent the code to — drives the code-only step's copy.
  const [otpMethod, setOtpMethod] = useState<"totp" | "email">("email");
  const [emailHint, setEmailHint] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { reduced, spread, ready, taglineIn, par, onMove } = useLoginIntro();

  if (status === "ready" && user) return <Navigate to={from} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await login({
      email,
      password,
      ...(otp ? { otp } : {}),
    });
    setSubmitting(false);
    // Both "ok" and "two_factor_required" prove email+password were
    // accepted upstream, so this is a real address worth remembering —
    // we never persist a typo from a failed attempt.
    if (result.kind === "ok" || result.kind === "two_factor_required") {
      setLastEmail(email);
    }
    if (result.kind === "ok") {
      navigate(from, { replace: true });
    } else if (result.kind === "two_factor_required") {
      // Step 1 cleared (email + password are good). Collapse to a
      // code-only step and remember how/where the code arrives.
      setNeedsOtp(true);
      setOtpMethod(result.method);
      setEmailHint(result.emailHint);
    } else {
      setError(result.message);
    }
  };

  /** Back out of the code step to re-enter credentials (e.g. wrong account).
   *  Keeps email/password so the form is intact; just drops the code + error. */
  const backToCredentials = () => {
    setNeedsOtp(false);
    setOtp("");
    setResent(false);
    setError(null);
  };

  /** Email method only: re-run the first leg (no OTP) so portal-api emails a
   *  fresh code. Reuses the same login call — a code-only step never has a
   *  separate "resend" endpoint. */
  const resendCode = async () => {
    setResending(true);
    setError(null);
    setResent(false);
    const result = await login({ email, password });
    setResending(false);
    if (result.kind === "two_factor_required") {
      setEmailHint(result.emailHint);
      setOtp("");
      setResent(true);
    } else if (result.kind === "ok") {
      // 2FA was turned off between attempts — just proceed.
      navigate(from, { replace: true });
    } else {
      setError(result.message);
    }
  };

  return (
    <Box
      component="main"
      onMouseMove={onMove}
      className="login-aurora"
      sx={{
        position: "relative",
        display: "grid",
        minHeight: "100vh",
        placeItems: "center",
        overflow: "hidden",
        px: 3,
        py: 5,
      }}
    >
      <LoginAurora spread={spread} par={par} />

      <Box sx={{ position: "relative", display: "flex", width: "100%", maxWidth: "24rem", flexDirection: "column", alignItems: "center" }}>
        <LoginHero ready={ready} taglineIn={taglineIn} reduced={reduced} par={par} />

        {/* Glass sign-in card — focus-in (blur → sharp + scale) once settled. */}
        <Box
          aria-hidden={!ready}
          sx={{
            mt: 4.5,
            width: "100%",
            transition: "opacity 820ms cubic-bezier(0.16,1,0.3,1), transform 820ms cubic-bezier(0.16,1,0.3,1), filter 820ms cubic-bezier(0.16,1,0.3,1)",
            ...(ready
              ? { transform: "translateY(0) scale(1)", opacity: 1, filter: "blur(0)" }
              : { pointerEvents: "none", transform: "translateY(20px) scale(0.96)", opacity: 0, filter: "blur(4px)" }),
          }}
        >
          <Box
            sx={{
              borderRadius: 1,
              border: 1,
              borderColor: (t) => alpha(t.palette.common.white, 0.15),
              bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
              p: 3,
              boxShadow: "0 24px 70px -24px rgba(0,0,0,0.65)",
              backdropFilter: "blur(12px)",
            }}
          >
            <Box component="header" sx={{ mb: 3, textAlign: "center" }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.01em", color: "text.primary" }}
              >
                Internal Dashboard
              </Typography>
              <Typography component="p" sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
                {needsOtp
                  ? "Two-step verification keeps your account secure."
                  : "Sign in to your command center."}
              </Typography>
            </Box>

            <Box component="form" onSubmit={onSubmit} sx={{ display: "grid", gap: 2 }}>
              {needsOtp ? (
                /* Step 2 — email + password already checked out. Show JUST the
                   code input, and tell the user where the code came from. */
                <>
                  <Typography
                    role="status"
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: (t) => alpha(t.palette.info.main, 0.35),
                      bgcolor: (t) => alpha(t.palette.info.main, 0.08),
                      px: 1.5,
                      py: 1.25,
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    {otpMethod === "email" ? (
                      <>
                        We emailed a 6-digit sign-in code to{" "}
                        <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                          {emailHint ?? "your email"}
                        </Box>
                        . Enter it below to finish signing in.
                      </>
                    ) : (
                      <>
                        Open your authenticator app and enter the 6-digit code
                        for XOP to finish signing in.
                      </>
                    )}
                  </Typography>
                  <Field
                    label="Verification code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      setResent(false);
                    }}
                    required
                    autoFocus
                    disabled={submitting}
                  />
                  {otpMethod === "email" && (
                    <TextLink onClick={() => void resendCode()} disabled={resending || submitting}>
                      {resending ? "Sending…" : resent ? "Code re-sent ✓" : "Resend code"}
                    </TextLink>
                  )}
                </>
              ) : (
                <>
                  <Field
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={setEmail}
                    required
                    autoFocus={!hadRememberedEmail.current}
                    disabled={submitting}
                  />
                  <Field
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={setPassword}
                    required
                    autoFocus={hadRememberedEmail.current}
                    disabled={submitting}
                    adornment={
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        disabled={submitting}
                        sx={{
                          display: "inline-flex",
                          height: 28,
                          width: 28,
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 0.5,
                          border: 0,
                          background: "none",
                          cursor: "pointer",
                          color: "text.disabled",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </Box>
                    }
                  />
                </>
              )}
              {error && (
                <Typography
                  role="alert"
                  sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: (t) => alpha(t.palette.error.main, 0.4),
                    bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                    px: 1.5,
                    py: 1,
                    fontSize: 12,
                    color: "error.main",
                  }}
                >
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={submitting}
                className="shadow-glow"
                sx={{ mt: 0.5, width: "100%", transition: "transform 150ms", "&:hover": { transform: "translateY(-1px)" } }}
              >
                {submitting ? "Signing in…" : needsOtp ? "Verify & sign in" : "Sign in"}
              </Button>
              {needsOtp && (
                <TextLink onClick={backToCredentials} disabled={submitting} sx={{ justifySelf: "center" }}>
                  ← Use a different account
                </TextLink>
              )}
            </Box>
          </Box>
          <Typography component="p" sx={{ mt: 2.5, textAlign: "center", fontSize: 12, color: (t) => alpha(t.palette.common.white, 0.55) }}>
            Trouble signing in? Contact your XOP administrator.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
